const META_PIXEL_ID = '1824848678883523'
export const META_CAPI_ENDPOINT =
  'https://vantixcreditsolutions.com/api/meta-event'

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
    __leadEnviado?: boolean
  }
}

export type MetaLeadUser = {
  firstName: string
  lastName: string
  email: string
  phone: string
  city: string
  state: string
  country: string
}

type MetaEventPayload = {
  event_name: 'PageView' | 'Lead'
  event_id: string
  fbp?: string
  fbc?: string
  event_source_url: string
  user?: MetaLeadUser
}

export function generateEventId() {
  return crypto.randomUUID()
}

export function getCookie(name: string) {
  const prefix = `${encodeURIComponent(name)}=`
  const cookie = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix))

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : undefined
}

export function normalizePhone(phone: string) {
  const digits = phone.replace(/\D/g, '')
  return digits.length === 10 ? `1${digits}` : digits
}

async function waitForFbp(maxWait = 500) {
  const startedAt = Date.now()
  while (!getCookie('_fbp') && Date.now() - startedAt < maxWait) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, 50))
  }
}

async function postMetaEvent(payload: MetaEventPayload) {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 1500)
  try {
    const response = await fetch(META_CAPI_ENDPOINT, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
      keepalive: true,
      signal: controller.signal,
    })
    if (!response.ok) {
      console.error('El endpoint de Meta CAPI no está disponible.')
    }
  } catch (error) {
    console.error('No se pudo enviar el evento a Meta CAPI.', error)
  } finally {
    window.clearTimeout(timeout)
  }
}

export async function trackPageView() {
  const eventId = generateEventId()
  window.fbq?.('track', 'PageView', {}, { eventID: eventId })
  await waitForFbp()

  await postMetaEvent({
    event_name: 'PageView',
    event_id: eventId,
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
    event_source_url: window.location.origin,
  })

  return eventId
}

export async function trackLead(
  user: MetaLeadUser,
  eventId = generateEventId(),
) {
  const email = user.email.trim().toLowerCase()
  const phone = normalizePhone(user.phone)
  const firstName = user.firstName.trim().toLowerCase()
  const lastName = user.lastName.trim().toLowerCase()
  const city = user.city.trim().toLowerCase().replace(/\s/g, '')
  const state = user.state.trim().toLowerCase()
  const country = user.country === 'Puerto Rico' ? 'pr' : 'us'

  window.fbq?.('init', META_PIXEL_ID, {
    em: email,
    ph: phone,
    fn: firstName,
    ln: lastName,
    ct: city,
    st: state,
    country,
  })

  window.fbq?.('track', 'Lead', {}, { eventID: eventId })
  await waitForFbp()

  await postMetaEvent({
    event_name: 'Lead',
    event_id: eventId,
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
    event_source_url: window.location.origin,
    user: {
      email,
      phone,
      firstName,
      lastName,
      city,
      state,
      country,
    },
  })

  return eventId
}
